/* eslint-disable indent */
import { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';

import { FormikErrors } from 'formik';
import { useOnClickOutside } from 'usehooks-ts';

import { Label, LABEL_TYPE, PARAGRAPH2_NORMAL_CLASSES } from '@/components/generic';
import { CalendarIcon } from '@/resources';
import { CreateEditEventPayload, CustomAttributeValue } from '@/types';

type Props = {
  id: number;
  isRequired: boolean;
  labelKey: string;
  type: string;
  customSymbol?: string;
  validationMessageKey: string;
  placeholderKey: string;
  position: number;
  errors: FormikErrors<CreateEditEventPayload>;
  setFieldValue: (
    field: string,
    value: CustomAttributeValue[],
    shouldValidate?: boolean | undefined
  ) => void;
  validateForm: (values?: CustomAttributeValue[]) => Promise<FormikErrors<CreateEditEventPayload>>;
  values: CreateEditEventPayload;
  defaultValue?: { id: number; name: string };
  isEditPage?: boolean;
  minDate?: Date;
  maxDate?: Date;
};

export const CustomAttributeField = ({
  id,
  isRequired,
  labelKey,
  type,
  customSymbol,
  validationMessageKey,
  placeholderKey,
  position,
  errors,
  setFieldValue,
  validateForm,
  values,
  defaultValue,
  isEditPage
}: Props) => {
  const [t] = useTranslation('common');
  const [touched, setTouched] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [customAttributeValue, setCustomAttributeValue] = useState<string>('');
  const [userChanged, setUserChanged] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const datePickerRef = useRef<HTMLDivElement | null>(null);
  const isSymbolApplied =
    !!customSymbol &&
    (customAttributeValue.startsWith(`${customSymbol} `) || customAttributeValue === customSymbol);

  const handleDateChange = (date: Date) => {
    if (!isDatePickerOpen) {
      return;
    }
    setSelectedDate(date);
    const formattedDate = date ? date.toLocaleDateString('en-GB') : '';
    updateCustomAttributeValue(formattedDate);
  };

  const updateCustomAttributeValue = (value: string) => {
    setIsDatePickerOpen(false);
    setCustomAttributeValue(value);
    setUserChanged(true);

    const customAttributeValues = values.customAttributeValues.map((ca) =>
      ca.customAttributeTemplateId === id ? { ...ca, value } : { ...ca }
    );
    setFieldValue('customAttributeValues', customAttributeValues);
  };

  const handleCustomSymbolToggle = () => {
    if (!customSymbol) {
      return;
    }

    const updatedValue = isSymbolApplied
      ? customAttributeValue.startsWith(`${customSymbol} `)
        ? customAttributeValue.slice(customSymbol.length + 1)
        : customAttributeValue.slice(customSymbol.length)
      : customAttributeValue.trim().length > 0
        ? `${customSymbol} ${customAttributeValue}`
        : `${customSymbol} `;

    updateCustomAttributeValue(updatedValue);
  };

  useEffect(() => {
    if (!userChanged) {
      if (defaultValue && !isEditPage) {
        setCustomAttributeValue(defaultValue.name);
        const indexForChange = values.customAttributeValues.findIndex(
          (ca) => ca.customAttributeTemplateId === defaultValue.id
        );
        values.customAttributeValues[indexForChange] = {
          ...values.customAttributeValues[indexForChange],
          value: defaultValue.name
        };
      } else {
        setCustomAttributeValue(
          values.customAttributeValues.find((ca) => ca.customAttributeTemplateId === id)?.value ||
            ''
        );
      }
    }
  }, [defaultValue, values, id, userChanged]);

  useEffect(() => {
    if (defaultValue?.name !== '') {
      validateForm();
    }
  }, [values.name]);

  useOnClickOutside(datePickerRef, () => {
    setIsDatePickerOpen(false);
  });

  const renderComponent = () => {
    switch (type) {
      case 'date':
        return (
          <div>
            <Label text={`${t(labelKey)}${isRequired ? ' *' : ''}`} type={LABEL_TYPE.PARAGRAPH2} />
            <div className={'flex h-10 w-full items-center'} ref={datePickerRef}>
              <DatePicker
                wrapperClassName='w-full h-full mt-3.5'
                selected={selectedDate}
                onChange={handleDateChange}
                scrollableYearDropdown
                yearDropdownItemNumber={150}
                showYearDropdown
                open={isDatePickerOpen}
                customInput={
                  <div
                    className={`flex w-full items-center rounded-[10px] border border-solid focus-within:border-2 focus-within:border-primary-blue ${
                      (errors?.customAttributeValues?.[
                        position - 1
                      ] as unknown as CustomAttributeValue) && touched
                        ? 'border-accent-red focus:outline-accent-red'
                        : 'border-secondary-blue-gray'
                    }`}>
                    <input
                      className={'ml-3 size-full outline-none'}
                      type='text'
                      placeholder={t(placeholderKey)}
                      value={customAttributeValue}
                      onChange={(e) => updateCustomAttributeValue(e.target.value)}
                    />
                    {customSymbol && (
                      <button
                        type='button'
                        onClick={handleCustomSymbolToggle}
                        className={`mr-1 flex size-7 items-center justify-center rounded transition-colors ${
                          isSymbolApplied
                            ? 'bg-primary-gray text-primary-blue'
                            : 'text-primary-black hover:bg-primary-gray active:bg-primary-gray'
                        }`}>
                        <Label
                          text={customSymbol}
                          type={LABEL_TYPE.PARAGRAPH2}
                          className='!text-lg !leading-none'
                        />
                      </button>
                    )}
                    <button
                      type='button'
                      className='mr-2 flex size-6 items-center justify-center text-primary-black'
                      onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}>
                      <CalendarIcon className='text-primary-black' />
                    </button>
                  </div>
                }
                popperPlacement='top'
                maxDate={new Date()}
              />
            </div>
            {!!(errors?.customAttributeValues?.[
              position - 1
            ] as unknown as CustomAttributeValue) && (
              <div className='mt-3 h-[15px] text-right'>
                <Label text={t(validationMessageKey)} type={LABEL_TYPE.PARAGRAPH_ERROR} />
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className={'space-y-2.5'}>
            <Label text={`${t(labelKey)}${isRequired ? ' *' : ''}`} type={LABEL_TYPE.PARAGRAPH2} />
            <div
              className={`box-border flex h-10 w-full items-center rounded-[10px] border border-solid bg-primary-white focus-within:border-2 focus-within:border-primary-blue ${
                (errors?.customAttributeValues?.[
                  position - 1
                ] as unknown as CustomAttributeValue) && touched
                  ? 'border-accent-red focus-within:border-accent-red'
                  : 'border-secondary-blue-gray'
              }`}>
              <input
                onBlur={() => setTouched(true)}
                className={`ml-3 size-full bg-transparent outline-none ${PARAGRAPH2_NORMAL_CLASSES}`}
                type='text'
                placeholder={t(placeholderKey)}
                value={customAttributeValue}
                onChange={(e) => updateCustomAttributeValue(e.target.value)}
              />
              {customSymbol && (
                <button
                  type='button'
                  onClick={handleCustomSymbolToggle}
                  className={`mr-2 flex size-7 items-center justify-center rounded transition-colors ${
                    isSymbolApplied
                      ? 'bg-primary-gray text-primary-blue'
                      : 'text-primary-black hover:bg-primary-gray active:bg-primary-gray'
                  }`}>
                  <Label
                    text={customSymbol}
                    type={LABEL_TYPE.PARAGRAPH2}
                    className='!text-lg !leading-none'
                  />
                </button>
              )}
            </div>
            <div className='h-[15px] text-right'>
              {!!(errors?.customAttributeValues?.[
                position - 1
              ] as unknown as CustomAttributeValue) &&
                touched && (
                  <Label text={t(validationMessageKey)} type={LABEL_TYPE.PARAGRAPH_ERROR} />
                )}
            </div>
          </div>
        );
    }
  };

  return renderComponent();
};
